/**
 * Treemap / sunburst builder (ADR-0079 §4.1, §4.4).
 *
 * Hierarchical chart. The renderer rolls upstream rows up into a tree per
 * `encoding.hierarchy` (outer → inner column), summing `value.column` at
 * each leaf and propagating sums up the tree.
 *
 * `chartType: "treemap"` uses ECharts's treemap series; `"sunburst"` uses
 * the sunburst series — same data shape, different visualization.
 */

import type { TreemapSpec } from "../echartsSpec.js";
import {
  buildNoDataGraphic,
  resolveColumnIndex,
  resolvePalette,
} from "./shared.js";

type EChartsOption = Record<string, unknown>;

type TreeNode = {
  name: string;
  value: number;
  children?: TreeNode[];
};

export function buildTreemapOption(
  spec: TreemapSpec,
  rows: unknown[][],
  dimensions: string[],
): EChartsOption {
  const isEmpty = rows.length === 0;

  const hierarchyIdxs = spec.encoding.hierarchy.map((h) =>
    resolveColumnIndex(h.column, dimensions),
  );
  const valueIdx = resolveColumnIndex(spec.encoding.value.column, dimensions);
  const cleanHierarchy = hierarchyIdxs.every((i) => i !== null);

  const data: TreeNode[] =
    cleanHierarchy && valueIdx !== null
      ? rollUp(rows, hierarchyIdxs as number[], valueIdx)
      : [];

  const palette = resolvePalette(spec.options.color, data.length);
  const seriesType = spec.chartType === "sunburst" ? "sunburst" : "treemap";

  const series: Record<string, unknown> = {
    type: seriesType,
    name: spec.encoding.value.column,
    data,
    ...(spec.options.levels !== undefined ? { leafDepth: spec.options.levels } : {}),
  };

  return {
    ...(spec.options.title
      ? {
          title: {
            text: spec.options.title,
            ...(spec.options.subtitle ? { subtext: spec.options.subtitle } : {}),
          },
        }
      : {}),
    ...(palette ? { color: palette } : {}),
    legend: { show: spec.options.legend?.show !== false },
    tooltip: {
      show: spec.options.tooltip?.show !== false,
      trigger: "item",
    },
    series: [series],
    ...(isEmpty ? { graphic: buildNoDataGraphic() } : {}),
  };
}

function rollUp(
  rows: unknown[][],
  hierarchyIdxs: number[],
  valueIdx: number,
): TreeNode[] {
  // Build a nested map keyed by the hierarchy values, then convert to
  // ECharts's TreeNode shape. Children are arrays of TreeNode.
  // map[level0][level1]...[levelN-1] = sumOfValue
  type Node = { value: number; children: Map<string, Node> };
  const root: Node = { value: 0, children: new Map() };

  for (const row of rows) {
    const v = coerce(row[valueIdx]);
    if (v == null) continue;
    let cursor = root;
    for (let i = 0; i < hierarchyIdxs.length; i++) {
      const cell = row[hierarchyIdxs[i]];
      const key = cell == null ? "(unknown)" : String(cell);
      let next = cursor.children.get(key);
      if (!next) {
        next = { value: 0, children: new Map() };
        cursor.children.set(key, next);
      }
      next.value += v;
      cursor = next;
    }
  }

  function toTree(name: string, node: Node): TreeNode {
    if (node.children.size === 0) return { name, value: node.value };
    const children: TreeNode[] = [];
    for (const [k, v] of node.children) children.push(toTree(k, v));
    return { name, value: node.value, children };
  }

  const out: TreeNode[] = [];
  for (const [k, v] of root.children) out.push(toTree(k, v));
  return out;
}

function coerce(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
